import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { AppState } from '../createStore';
import {
  countItemsInCart,
  CouponSuccess,
  Dispatch,
  getDisplayableDiscounts,
  getCatalogDisplayableOrderItem,
  getDisplayableOrderItem,
  getMinOrderPriceDetails,
  getPriceComponentsAndOrderCharges,
  GetPriceComponentsAndOrderChargesArgs,
  ItemsHash,
  Menu,
  OrderItem,
  Restaurant,
  Tip,
  getMaxRedeemablePoints,
  convertCentsToLoyaltyPoints,
  convertLoyaltyPointsToCents,
  DispatchInfo,
  ChargeV2,
  Catalog,
  CatalogPlatform,
  CatalogItemsHash,
} from '@wix/restaurants-client-logic';
import { CalculatedFee } from '@wix/ambassador-service-fees-rules/types';
import { loyaltyProgramSelector } from './loyaltyProgramSelector';
import { deliveryPartnerFeeSelector } from './deliveryProviderFeeSelector';
import { selectIsCatalogsV3 } from './experimentsSelector';

function cartSummarySelectorInner(
  dispatch: Dispatch,
  catalog: Catalog,
  menu: Menu,
  charges: ChargeV2[],
  orderItems: OrderItem[],
  isMobile: boolean,
  restaurant: Restaurant,
  catalogItemsHash: CatalogItemsHash,
  itemsHash: ItemsHash,
  loyaltyProgram: ReturnType<typeof loyaltyProgramSelector>,
  coupon?: CouponSuccess,
  tip?: Tip,
  loyaltyPointsToRedeem?: number,
  deliveryPartnerFee?: number,
  isCatalogsV3?: boolean,
  calculatedFees?: CalculatedFee[],
  idealDeliveryArea?: DispatchInfo,
) {
  const dispatchType = dispatch.type;
  const dispatchTime = moment(dispatch.time).tz(restaurant.timezone);
  const platform = isMobile ? 'mobileweb' : 'web';
  const chargesV2 = charges || [];
  const address = dispatch.type === 'delivery' ? dispatch.address : undefined;
  const couponHashCode = coupon?.couponHashCode;
  const getPriceComponentsAndOrderChargesArgs: GetPriceComponentsAndOrderChargesArgs = {
    dispatchType,
    dispatchTime,
    orderItems,
    platform,
    chargesV2,
    restaurant,
    address,
    couponHashCode,
    tip,
    redeemAmount: 0,
    deliveryPartnerProps: {
      deliveryPartnerFee,
      shouldConsiderDeliveryPartner: true,
    },
    // [TODO] - Remove the next line once we remove this option from client-logic https://app.asana.com/0/0/1203264831523217/f
    shouldCalculateTipFromSubtotal: true,
    calculatedFees,
  };

  const {
    priceComponents: { subtotal: rawSubtotal },
  } = getPriceComponentsAndOrderCharges(getPriceComponentsAndOrderChargesArgs);

  const { isLoyaltyRedeemActive, pointsRequired, pointsBalance, currencyValue } = loyaltyProgram;

  const { maxRedeemableCents, maxRedeemablePoints } = getMaxRedeemablePoints({
    subtotal: rawSubtotal,
    pointsRequired,
    pointsBalance,
    currencyValue,
  });

  const points =
    typeof loyaltyPointsToRedeem === 'number' && loyaltyPointsToRedeem > 0
      ? convertLoyaltyPointsToCents({ pointsAmount: loyaltyPointsToRedeem, pointsRequired, currencyValue })
      : 0;

  const redeemAmount =
    isLoyaltyRedeemActive && loyaltyPointsToRedeem !== 0 ? Math.min(maxRedeemableCents, points || Number.MAX_VALUE) : 0;

  const redeemPoints = convertCentsToLoyaltyPoints({ centsAmount: redeemAmount, pointsRequired, currencyValue });

  const { orderCharges, priceComponents, displayablePriceComponents } = getPriceComponentsAndOrderCharges({
    ...getPriceComponentsAndOrderChargesArgs,
    redeemAmount,
  });

  const displayableDiscounts = isCatalogsV3
    ? getDisplayableDiscounts({
        chargesV2,
        dispatchTime,
        dispatchType,
        totalOrderPrice: priceComponents.total,
        platform,
        couponHashCode,
        orderCharges,
        restaurant,
      })
    : getDisplayableDiscounts({
        menu,
        dispatchTime,
        dispatchType,
        totalOrderPrice: priceComponents.total,
        platform,
        couponHashCode,
        orderCharges,
        restaurant,
      });

  const displayableOrderItems = orderItems.map((orderItem) =>
    isCatalogsV3
      ? getCatalogDisplayableOrderItem({
          catalog,
          dispatchTime: moment(dispatchTime).tz(restaurant.timezone),
          platform: isMobile ? CatalogPlatform.MOBILE_SITE : CatalogPlatform.SITE,
          orderItem,
          itemsHash: catalogItemsHash,
          currency: restaurant.currency,
          locale: restaurant.locale,
          dispatchType,
        })
      : getDisplayableOrderItem({
          menu,
          dispatchTime: moment(dispatchTime).tz(restaurant.timezone),
          platform: isMobile ? 'mobileweb' : 'web',
          orderItem,
          itemsHash,
          currency: restaurant.currency,
          locale: restaurant.locale,
          dispatchType,
        }),
  );
  const itemCount = countItemsInCart(displayableOrderItems);

  const { displayableMinOrderPrice } = getMinOrderPriceDetails({
    totalOrderPrice: priceComponents.subtotal,
    restaurant,
    dispatchType,
    idealDeliveryArea,
    shouldConsiderDeliveryPartner: true,
  });

  return {
    dispatchTime,
    totalOrderPrice: priceComponents.total,
    orderCharges,
    priceComponents,
    displayableDiscounts,
    displayablePriceComponents,
    displayableOrderItems,
    itemCount,
    minOrderPrice: displayableMinOrderPrice,
    redeemCents: redeemAmount,
    redeemPoints,
    maxRedeemableCents,
    maxRedeemablePoints,
  };
}

export const cartSummarySelector = createSelector(
  [
    (state: AppState) => state.checkout.dispatch,
    (state: AppState) => state.session.catalog,
    (state: AppState) => state.session.menu,
    (state: AppState) => state.session.charges,
    (state: AppState) => state.cart.orderItems,
    (state: AppState) => state.platformParams.isMobile,
    (state: AppState) => state.session.restaurant,
    (state: AppState) => state.session.catalogItemsHash,
    (state: AppState) => state.session.itemsHash,
    (state: AppState) => loyaltyProgramSelector(state),
    (state: AppState) => (state.cart.coupon?.type === 'success' && state.cart.coupon) || undefined,
    (state: AppState) =>
      state.checkout.pendingCashierPayment.paymentMethod !== 'offline' ? state.checkout.tip : undefined,
    (state: AppState) => state.checkout.loyaltyPointsToRedeem,
    (state: AppState) => deliveryPartnerFeeSelector(state),
    (state: AppState) => selectIsCatalogsV3(state),
    (state: AppState) => state.checkout.calculatedFees,
  ],
  cartSummarySelectorInner,
);
